import { useEffect, useState } from 'react';
import {
  Box,
  HStack,
  Image,
  LinkBox,
  LinkOverlay,
  Skeleton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { ExternalLink } from 'lucide-react';
import { api, type LinkPreview } from '../../lib/api';
import { previewImageSrc } from '../../lib/previewImage';

// Lazy, on-view unfurl (family-apps spec §4.1): no fetch at send time and no
// fan-out. A module-level cache means scrolling back never re-requests.
const cache = new Map<string, LinkPreview | null>();
const inflight = new Map<string, Promise<LinkPreview | null>>();

function loadPreview(url: string): Promise<LinkPreview | null> {
  const cached = cache.get(url);
  if (cached !== undefined) return Promise.resolve(cached);

  const pending = inflight.get(url);
  if (pending) return pending;

  const request = api
    .getLinkPreview(url)
    .then((response) => (response.data.status === 'ok' ? response.data : null))
    .catch(() => null)
    .then((preview) => {
      cache.set(url, preview);
      inflight.delete(url);
      return preview;
    });

  inflight.set(url, request);
  return request;
}

export function LinkPreviewCard({
  url,
  sealed,
}: {
  url: string;
  /**
   * M28 (D61): an encrypted message carries its own preview (or none); the
   * device never asks the server about its links.
   */
  sealed?: { preview: LinkPreview | null };
}) {
  const [preview, setPreview] = useState<LinkPreview | null | undefined>(
    sealed ? sealed.preview : cache.get(url)
  );
  const borderColor = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const subtle = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (sealed) {
      setPreview(sealed.preview);
      return;
    }
    let cancelled = false;
    setPreview(cache.get(url));

    loadPreview(url).then((result) => {
      if (!cancelled) setPreview(result);
    });

    return () => {
      cancelled = true;
    };
    // `sealed` is fixed for a message; its preview is read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  if (preview === undefined) {
    return <Skeleton mt={2} borderRadius="md" h="72px" w="260px" maxW="100%" />;
  }

  if (!preview) return null;
  const imageSrc = previewImageSrc(preview);

  return (
    <LinkBox
      mt={2}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      overflow="hidden"
      bg="blackAlpha.50"
      _dark={{ bg: 'whiteAlpha.100' }}
      maxW="360px"
    >
      {/* Only the sealed copy; the site's address is never loaded (D320). */}
      {imageSrc && (
        <Image src={imageSrc} alt="" w="full" maxH="160px" objectFit="cover" />
      )}
      <Box p={3}>
        <Text
          fontSize="xs"
          color={subtle}
          textTransform="uppercase"
          noOfLines={1}
        >
          {preview.siteName ?? new URL(preview.url).hostname}
        </Text>
        <HStack align="start" spacing={2} mt={1}>
          <LinkOverlay href={preview.url} isExternal>
            <Text fontWeight="semibold" fontSize="sm" noOfLines={2}>
              {preview.title ?? preview.url}
            </Text>
          </LinkOverlay>
          <ExternalLink size={14} />
        </HStack>
        {preview.description && (
          <Text fontSize="sm" color={subtle} noOfLines={3} mt={1}>
            {preview.description}
          </Text>
        )}
      </Box>
    </LinkBox>
  );
}
